import { useState } from "react";
import { api, ApiError } from "../api";
import { useScoreRun } from "./ScoreRunContext";

// Asking for a folder to be scored.
//
// WHY THIS IS A BUTTON AND NOT AN AUTOMATION. Scoring spends real money — about a quarter a
// paper — and a folder is re-read whenever a teacher presses check. Scoring on arrival would bill
// a class every time somebody tidied a Drive folder. Somebody decides, and that somebody is the
// person who knows whether the work is finished.
//
// WHAT IT PROMISES. That a job was asked for, and how many papers were waiting when it was. Never
// when it will finish, which is unknowable. WHETHER IT IS STILL GOING is Cloud Run's fact and is
// now asked for rather than guessed — `ScoreRunContext` polls while a run is live and stops when
// it is not, which is how the people list can say a paper is queued rather than merely unscored.
// The counts below are still artifacts, true by construction: a paper that is scored is in the
// scored state, whatever any run record claims.

/** The states a paper passes through, in the order it passes through them. `bound` is the queue;
 *  everything after it has been scored at least once. */
const DONE = ["scored", "composed", "blocked", "in_review", "released", "withheld",
              "not_scorable"];

export function ScoreRun({ activityId }: { activityId: string | null }) {
  // READ FROM THE SHARED FETCH. The people list needs the same answer to say where each paper is
  // in the line, and two components polling one endpoint would ask Cloud Run twice as often for a
  // fact neither of them owns.
  const { state, refresh: read } = useScoreRun();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  const ask = () => {
    if (!activityId) return;
    setBusy(true); setError(null); setSaid(null);
    api.post<{ papers_waiting: number; joined: boolean }>("/score-runs",
                                                          { activity_id: activityId })
      .then((r) => {
        // JOINED IS NOT A FAILURE, and the sentence says which happened. One run scores
        // everything waiting in the district, so a second press during one is answered by the
        // run already going.
        setSaid(r.joined
          ? `${r.papers_waiting} paper${r.papers_waiting === 1 ? "" : "s"} added to the run `
            + `already going.`
          : `Scoring started on ${r.papers_waiting} paper${r.papers_waiting === 1 ? "" : "s"}.`);
        read();
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "could not be started"))
      .finally(() => setBusy(false));
  };

  if (!state || !state.available) return null;
  // Nobody has wired a scoring job to this deployment, or this lesson has no rubric. Neither is
  // something a teacher can act on, so neither gets a button that would refuse them.
  if (!state.configured || !state.scorable) return null;

  const done = DONE.reduce((n, s) => n + (state.states[s] ?? 0), 0);
  const total = done + state.waiting;

  // NOTHING COLLECTED YET IS NOT THIS CONTROL'S NEWS. The screen already says the class is empty
  // and that nothing has been scored; a third sentence saying nothing is waiting added no fact and
  // one more thing to read. It said it as a DISABLED BUTTON, so the only control on the strip was
  // a sentence you could not press.
  if (total === 0) return null;

  return (
    <div className="sr">
      {/* A button when there is something to press, and a sentence when there is not. */}
      {state.waiting === 0 ? (
        <span className="sr-idle">Everything collected has been scored.</span>
      ) : (
        <button className="btn" disabled={busy} onClick={ask}>
          {busy ? "Asking…" : `Score ${state.waiting} paper${state.waiting === 1 ? "" : "s"}`}
        </button>
      )}
      {total > 0 && (
        // The count a teacher actually asked about. It comes from the papers themselves, so it
        // stays true whether or not a run is still going.
        <span className="sr-count">{done} of {total} scored</span>
      )}
      {said && <span className="sr-said">{said}</span>}
      {error && <span className="inc-error">{error}</span>}
    </div>
  );
}
